(() => {
  if (customElements.get('sm-complete-look')) return;
  const parse = (value, fallback) => { try { return JSON.parse(value) ?? fallback; } catch { return fallback; } };
  const colour = (value = '') => {
    const text = value.toLowerCase();
    if (/raspberry|fuchsia|hot pink/.test(text)) return 'raspberry';
    if (/mint/.test(text)) return 'mint';
    if (/aqua|turquoise/.test(text)) return 'aqua';
    if (/pink|blush/.test(text)) return 'pink';
    if (/blue|sapphire|navy/.test(text)) return 'blue';
    if (/green|emerald/.test(text)) return 'green';
    if (/black|slate/.test(text)) return 'black';
    if (/purple|lilac|lavender/.test(text)) return 'purple';
    if (/red|ruby|wine/.test(text)) return 'red';
    if (/champagne|brown/.test(text)) return 'champagne';
    if (/clear|white|pearl/.test(text)) return 'clear';
    return '';
  };
  const metal = (profile, title, group = '') => {
    if (profile.metal && profile.metal !== 'variant') return profile.metal;
    if (/silver/i.test(title)) return 'silver';
    if (/gold/i.test(title)) return 'gold';
    if (group.includes('silver')) return 'silver';
    if (group.includes('gold')) return 'gold';
    return '';
  };
  const category = (type = '') => {
    const text = type.toLowerCase();
    if (/set/.test(text)) return 'set';
    if (/earring|tops/.test(text)) return 'earrings';
    if (/ring/.test(text)) return 'ring';
    if (/anklet|payal/.test(text)) return 'anklet';
    if (/hand chain/.test(text)) return 'hand-chain';
    if (/necklace|pendant/.test(text)) return 'necklace';
    if (/bracelet|bangle|karay|churi/.test(text)) return 'bracelet';
    return '';
  };
  class CompleteLook extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.form = document.getElementById(this.dataset.sourceForm);
      this.profile = parse(this.dataset.sourceProfile, {});
      this.cartProducts = new Set(parse(this.dataset.cartProducts, []).map(String));
      this.cards = [...this.querySelectorAll('[data-look-card]')].map(node => ({
        node, profile: parse(node.dataset.profile, {}), variants: parse(node.querySelector('[data-look-variants]')?.textContent, []),
        input: node.querySelector('[name="id"]'), chosenByCustomer: false
      }));
      this.form?.addEventListener('change', event => {
        if (!event.target.closest('[data-option-position]')) return;
        this.cards.forEach(card => { card.chosenByCustomer = false; });
        queueMicrotask(() => this.refresh());
      }, opts);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-look-select]')) return;
        const card = this.cards.find(item => item.node.contains(event.target));
        if (!card) return;
        card.chosenByCustomer = true;
        this.paint(card, card.variants.find(item => String(item.id) === event.target.value));
      }, opts);
      document.addEventListener('sm:cart-updated', async () => {
        const request = (this.cartRequest || 0) + 1;
        this.cartRequest = request;
        try {
          const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } });
          if (!response.ok) return;
          const cart = await response.json();
          if (request !== this.cartRequest || !this.isConnected) return;
          this.cartProducts = new Set(cart.items.map(item => String(item.product_id)));
          this.refresh();
        } catch {}
      }, opts);
      this.refresh();
    }
    disconnectedCallback() { this.controller?.abort(); this.ready = false; }
    paint(card, variant) {
      if (!variant) return;
      card.input.value = String(variant.id);
      const price = card.node.querySelector('[data-look-price]');
      if (price) price.textContent = variant.price;
      if (card.input.matches('select')) {
        const measure = document.createElement('span');
        const style = getComputedStyle(card.input);
        measure.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;';
        measure.style.font = style.font;
        measure.style.letterSpacing = style.letterSpacing;
        measure.textContent = card.input.selectedOptions[0]?.textContent || '';
        document.body.appendChild(measure);
        card.input.style.width = `${Math.ceil(measure.getBoundingClientRect().width) + 54}px`;
        measure.remove();
      }
      const button = card.node.querySelector('[type="submit"]');
      button.setAttribute('aria-label', `${button.textContent.trim()}: ${button.dataset.lookProductTitle || ''} — ${variant.title} — ${variant.price}`);
      card.node.querySelectorAll('[data-look-link]').forEach(link => { link.href = variant.url; });
      const img = card.node.querySelector('img');
      if (img && variant.image) { img.removeAttribute('srcset'); img.src = variant.image; }
      card.node.querySelector('[type="submit"]').disabled = !variant.available;
    }
    refresh() {
      const variants = parse(this.closest('[data-product-info]')?.querySelector('[data-sm-variants-json]')?.textContent, []);
      const selected = variants.find(item => String(item.id) === this.form?.querySelector('[name="id"]')?.value);
      const title = selected?.options?.join(' ') || '';
      const sourceMetal = metal(this.profile, title, this.dataset.sourceGroup);
      const sourceColour = this.profile.colour || colour(title);
      const sourceCategory = this.profile.category || category(this.dataset.sourceType);
      let count = 0;
      const shown = new Set();
      this.cards.forEach(card => {
        const profile = this.dataset.cartMode ? parse(card.node.dataset.sourceProfile, {}) : this.profile;
        const cardTitle = this.dataset.cartMode ? card.node.dataset.sourceTitle : title;
        const sourceMetal = metal(profile, cardTitle, card.node.dataset.sourceGroup || this.dataset.sourceGroup);
        const sourceColour = profile.colour || colour(cardTitle);
        const sourceCategory = profile.category || category(this.dataset.sourceType);
        const targetCategory = card.profile.category || category(card.node.dataset.productType);
        let eligible = !this.cartProducts.has(card.node.dataset.productId) && card.node.dataset.productId !== this.dataset.sourceProduct;
        if (sourceCategory && sourceCategory === targetCategory) eligible = false;
        if ((profile.includes || []).includes(targetCategory)) eligible = false;
        if (sourceCategory === 'set' && !profile.includes) eligible = false;
        const ranked = card.variants.filter(v => v.available).map(v => {
          const targetMetal = metal(card.profile, v.title, card.profile.group);
          if (sourceMetal && targetMetal && sourceMetal !== 'neutral' && targetMetal !== 'neutral' && sourceMetal !== targetMetal) return null;
          const targetColour = card.profile.colour || colour(v.title);
          if (sourceColour && sourceColour !== 'clear' && targetColour && targetColour !== 'clear' && targetColour !== sourceColour) return null;
          if ((!sourceColour || sourceColour === 'clear') && targetColour && targetColour !== 'clear' && !card.profile.colour) return null;
          let score = targetColour === sourceColour && sourceColour ? 10 : targetColour === 'clear' ? 5 : 1;
          if (card.chosenByCustomer && String(v.id) === card.input.value) score += 100;
          return { variant: v, score };
        }).filter(Boolean).sort((a,b) => b.score-a.score);
        eligible = eligible && ranked.length > 0 && count < (this.dataset.cartMode ? 3 : 2) && !shown.has(card.node.dataset.productId);
        card.node.hidden = !eligible;
        if (eligible) { this.paint(card, ranked[0].variant); count += 1; shown.add(card.node.dataset.productId); }
      });
      this.hidden = count === 0;
    }
  }
  customElements.define('sm-complete-look', CompleteLook);
})();
